<script setup>
import { computed } from 'vue'
import { useFormSize } from 'element-plus'

const props = defineProps({
  id: {
    type: [Number, String],
    required: true
  },
  name: {
    type: String,
    default: ''
  },
  avatar: {
    type: String,
    default: ''
  },
  closable: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['close'])

const formSize = useFormSize()
const sizeClass = computed(() => {
  const s = formSize.value || 'default'
  if (s === 'small') return 'is-small'
  if (s === 'large') return 'is-large'
  return 'is-default'
})

const displayName = computed(() => props.name || '未知用户')
const firstChar = computed(() => {
  const s = String(displayName.value || '')
  return s.charAt(0) || '?'
})

const onClose = () => emit('close', props.id)
const onAvatarError = (e) => {
  e.target.style.display = 'none'
}
</script>

<template>
  <span class="user-tag" :class="[sizeClass, { 'is-disabled': disabled }]" :title="displayName">
    <span class="user-tag-avatar">
      <img v-if="avatar" :src="avatar" :alt="displayName" @error="onAvatarError">
      <span v-else class="user-tag-avatar-char">{{ firstChar }}</span>
    </span>
    <span class="user-tag-name">{{ displayName }}</span>
    <button
      v-if="closable && !disabled"
      type="button"
      class="user-tag-close"
      aria-label="移除"
      @click="onClose"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </span>
</template>

<style scoped lang="scss">
.user-tag {
  --tag-h: 30px;
  --tag-pad-x: 4px;
  --tag-gap: 6px;
  --tag-avatar: 22px;
  --tag-font: 13px;

  display: inline-flex;
  align-items: center;
  gap: var(--tag-gap);
  max-width: 100%;
  height: var(--tag-h);
  padding: 0 var(--tag-pad-x);
  border: 1px solid #dbeafe;
  border-radius: 9999px;
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: var(--tag-font);
  line-height: 1;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, opacity 0.16s ease;

  &:hover {
    border-color: #bfdbfe;
    background-color: #dbeafe;
  }

  &.is-small {
    --tag-h: 24px;
    --tag-pad-x: 3px;
    --tag-gap: 4px;
    --tag-avatar: 18px;
    --tag-font: 12px;
  }

  &.is-large {
    --tag-h: 34px;
    --tag-pad-x: 6px;
    --tag-gap: 8px;
    --tag-avatar: 26px;
    --tag-font: 14px;
  }

  &.is-disabled {
    background-color: #f1f5f9;
    border-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
    opacity: 0.85;
  }
}

.user-tag-avatar {
  flex: 0 0 var(--tag-avatar);
  width: var(--tag-avatar);
  height: var(--tag-avatar);
  border-radius: 9999px;
  overflow: hidden;
  background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%);
  color: #1d4ed8;
  font-size: calc(var(--tag-avatar) * 0.55);
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.user-tag-avatar-char {
  line-height: 1;
  text-transform: uppercase;
}

.user-tag-name {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.user-tag.is-small .user-tag-name {
  max-width: 100px;
}

.user-tag.is-large .user-tag-name {
  max-width: 180px;
}

.user-tag-close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  margin-right: 2px;
  border: 0;
  border-radius: 9999px;
  background-color: transparent;
  color: currentColor;
  opacity: 0.55;
  cursor: pointer;
  transition: opacity 0.16s ease, background-color 0.16s ease;

  svg {
    width: 10px;
    height: 10px;
  }

  &:hover {
    opacity: 1;
    background-color: rgba(37, 99, 235, 0.15);
  }
}

.user-tag.is-small .user-tag-close {
  width: 14px;
  height: 14px;

  svg {
    width: 9px;
    height: 9px;
  }
}
</style>
